import { lazy, useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'

import { ReactComponent as IconInfo } from '@/assets/images/icons/icon-info.svg'
import psbBanner from '@/assets/images/psb_banner_zabeg_wide.webp'

import PartnerInfo from '@/components/Modals/PartnerInfo/PartnerInfo'

import api from '@/api'
import NotFound from '@/pages/NotFound/NotFound'
import {
  getStoreCities,
  setStoreSelectedCity,
  setStoreAbid,
  setStoreFormats,
  setStoreCityFromFormats,
  getStoreSelectedCity,
} from '@/store/slices/app'

import styles from './Main.module.scss'

const Header = lazy(() => import('@/components/Header/Header'))
const Support = lazy(() => import('@/components/Support'))
const Head = lazy(() => import('@/components/Head/Head'))
const About = lazy(() => import('@/components/About/About'))
const StatLines = lazy(() => import('@/components/StatLines/StatLines'))
const Features = lazy(() => import('@/components/Features/Features'))
const Formats = lazy(() => import('@/components/Formats/Formats'))
const Maps = lazy(() => import('@/components/Maps/Maps'))
const StartPack = lazy(() => import('@/components/StartPack/StartPack'))
const FunZone = lazy(() => import('@/components/FunZone/FunZone'))
const Activities = lazy(() => import('@/components/Activities/Activities'))
const RunningCapital = lazy(() => import('@/components/RunningCapital/RunningCapital'))
const Medical = lazy(() => import('@/components/Medical/Medical'))
const Limits = lazy(() => import('@/components/Limits/Limits'))
const Faq = lazy(() => import('@/components/Faq/Faq'))
const JoinTeam = lazy(() => import('@/components/JoinTeam/JoinTeam'))
const Partners = lazy(() => import('@/components/Partners/Partners'))
const Record = lazy(() => import('@/components/Record/Record'))
const Footer = lazy(() => import('@/components/Footer/Footer'))

const mainPartner = {
  reklama: {
    name: 'ПАО «Промсвязьбанк»',
    inn: 'ИНН: 7744000912',
    gov_id: 'erid: 2VtzqvXsUPu',
  },
}

function Main() {
  const [isOpenPartnerInfo, setIsOpenPartnerInfo] = useState(false)
  const [isCityValid, setIsCityValid] = useState(true)

  const { cityName } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const cities = useSelector(getStoreCities)
  const selectedCity = useSelector(getStoreSelectedCity)

  useEffect(() => {
    setIsCityValid(true)

    if (!cities || cities.length === 0) {
      return
    }

    const getFormats = (publicId) => {
      api
        .get(`/api/event_format/event/${publicId}`)
        .then((r) => {
          dispatch(setStoreFormats(r.data.values))
          dispatch(setStoreCityFromFormats(r.data.city))
        })
        .catch((err) => console.error('Error fetching formats:', err))
    }

    const setCityAndFetchFormats = (city) => {
      if (city) {
        dispatch(setStoreSelectedCity(city))
        getFormats(city.public_id)
        localStorage.setItem('city', JSON.stringify(city))
        if (city.abid) {
          dispatch(setStoreAbid(city.abid))
        }
      }
    }

    const defaultCityPublicId = 'zabeg2025_524901'
    let foundCity = null

    if (cityName) {
      const decodedCityName = decodeURIComponent(cityName).toLowerCase()
      foundCity = cities.find((item) => item.city?.name_ru?.toLowerCase() === decodedCityName)
      if (foundCity) {
        setCityAndFetchFormats(foundCity)
        return
      } else {
        console.warn(`[Main Effect Step 1 Fail] City name "${decodedCityName}" from URL not found.`)
        setIsCityValid(false)
        return
      }
    }

    const localCityData = localStorage.getItem('city')
    if (localCityData) {
      try {
        const parsedLocalCity = JSON.parse(localCityData)
        foundCity = cities.find((item) => item.public_id === parsedLocalCity?.public_id)
        if (foundCity) {
          navigate(`/${encodeURIComponent(foundCity.city.name_ru)}`, { replace: true })
          return
        } else {
          localStorage.removeItem('city')
        }
      } catch {
        localStorage.removeItem('city')
      }
    }

    api
      .get('/api/location/detailed')
      .then((r) => {
        const geoCityId = r.data?.info?.city?.id
        if (geoCityId) {
          foundCity = cities.find((item) => item.city?.id === geoCityId)
          if (foundCity) {
            navigate(`/${encodeURIComponent(foundCity.city.name_ru)}`, { replace: true })
            return
          }
        }

        foundCity = cities.find((item) => item.public_id === defaultCityPublicId)
        if (foundCity) {
          navigate(`/${encodeURIComponent(foundCity.city.name_ru)}`, { replace: true })
          return
        }
      })
      .catch((err) => {
        console.error('Error fetching geolocation or fallback needed:', err)
        foundCity = cities.find((item) => item.public_id === defaultCityPublicId)
        if (foundCity) {
          navigate(`/${encodeURIComponent(foundCity.city.name_ru)}`, { replace: true })
        }
      })
  }, [cities, cityName, dispatch, navigate])

  const handleChangeCity = useCallback(
    (cityPublicId) => {
      const findCity = cities?.find((item) => item.public_id === cityPublicId)
      if (findCity) {
        navigate(`/${encodeURIComponent(findCity.city.name_ru)}`)
      }
    },
    [cities, navigate]
  )

  const pageTitle = selectedCity?.city?.name_ru
    ? `ЗаБег.РФ ${selectedCity.city.name_ru} | Всероссийский Полумарафон`
    : 'ЗаБег.РФ - Всероссийский Полумарафон'

  if (!isCityValid) {
    return <NotFound />
  }

  const handleOpenPartnerInfo = (e) => {
    e.preventDefault()
    setIsOpenPartnerInfo(true)
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`Присоединяйтесь к самому масштабному полумарафону России - ЗаБег.РФ ${selectedCity?.city?.name_ru ? `в городе ${selectedCity.city.name_ru}` : ''}! Синхронный старт по всей стране. Узнайте о форматах участия, трассах и регистрации.`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SportsEvent',
            name: 'Всероссийский полумарафон ЗаБег.РФ',
            description:
              'Самый масштабный полумарафон с синхронным стартом во всей стране! Более 150 000 участников. Присоединяйтесь!',
            url: 'https://забег.рф/',
            image: 'https://забег.рф/zabeg.jpg',
            startDate: '2025-05-24',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
            location: {
              '@type': 'Place',
              name: 'Города России и мира',
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'RU',
              },
            },
            organizer: {
              '@type': 'Organization',
              name: 'АНО ГОНКА ГЕРОЕВ',
              url: 'https://heroleague.ru/',
            },
          })}
        </script>
      </Helmet>
      <Header onChangeCity={handleChangeCity} />
      <Head />
      <About />
      <StatLines />
      <Features />
      <Formats onChangeCity={handleChangeCity} />
      <Maps />
      <StartPack />
      <div className={styles.psbBannerWrap}>
        <a href="https://www.psbank.ru/personal/page-zabegrf" target="_blank" rel="noopener noreferrer">
          <div className={styles.psbBannerInner}>
            <img className={styles.psbBanner} src={psbBanner} alt="Банк ПСБ - генеральный спонсор «Забег.РФ»" />

            <button className={styles.btnInfo} onClick={handleOpenPartnerInfo}>
              <span className="visually-hidden">Информация о партнёре</span>
              <IconInfo width={20} height={20} />
            </button>
          </div>
        </a>
      </div>
      <FunZone />
      <Activities />
      <RunningCapital />
      <Medical />
      <Limits />
      <JoinTeam />
      <Support />
      <Faq />
      <Partners />
      <Record />
      <Footer />

      <PartnerInfo partner={mainPartner} isOpen={isOpenPartnerInfo} onClose={() => setIsOpenPartnerInfo(false)} />
    </>
  )
}

export default Main
